import config from '../config';

export class BlueBannerService {
  constructor(msalOrCtx) {
    if (msalOrCtx && (msalOrCtx.msal || msalOrCtx.$msal)) {
      this.msal = msalOrCtx.msal || null; // holds idToken
      this.$msal = msalOrCtx.$msal || null; // holds signIn/refreshToken
    } else {
      this.msal = msalOrCtx;
      this.$msal = null;
    }
    this.url = config.apiUrl;
  }

  async handleResponse(res) {
    // Successful response (2xx)
    if (res.status >= 200 && res.status < 300) {
      const contentType = res.headers.get('content-type');
      const contentLength = res.headers.get('content-length');

      if (res.status === 204 || contentLength === '0') {
        return null;
      }

      if (!contentType || !contentType.toLowerCase().startsWith('application/json')) {
        try {
          const text = await res.text();
          return text || null;
        } catch (_) {
          return null;
        }
      }

      try {
        return await res.json();
      } catch (e) {
        try {
          const text = await res.text();
          return text || null;
        } catch (_) {
          return null;
        }
      }
    }
    
    // Handle 401 Unauthorized - try to refresh token via $msal if available
    if (res.status === 401) {
      console.log('Received 401, attempting to refresh token...');
      try {
        if (this.$msal && typeof this.$msal.refreshToken === 'function') {
          await this.$msal.refreshToken();
        }
        // Retry the request with new token
        const newHeaders = this.getHeaders();
        const retryOptions = {
          method: res.method || 'GET',
          headers: newHeaders
        };
        if (res.body && res.method && !['GET', 'HEAD'].includes(res.method.toUpperCase())) {
          retryOptions.body = res.body;
        }
        const retryRes = await fetch(res.url, retryOptions);
        return this.handleResponse(retryRes);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, attempt sign-in via $msal if available
        if (this.$msal && typeof this.$msal.signIn === 'function') {
          await this.$msal.signIn();
        }
        throw new Error('Authentication required');
      }
    }
    
    const isJson = res.headers.get('content-type')?.toLowerCase().startsWith('application/json');
    if (isJson) {
      return res.json().then(body => Promise.reject(body));
    }
    const text = await res.text();
    return Promise.reject(text);
  }

  serializeBody(data) {
    if (data instanceof FormData) return { body: data };
    return { contentType: 'application/json', body: JSON.stringify(data) };
  }

  getHeaders() {
    const token = this.msal?.idToken;
    if (!token) {
      throw new Error('No authentication token available');
    }
    return { 'Authorization': `Bearer ${token}` };
  }

  async getBannerConfig() {
    const res = await fetch(`${this.url}/ArenaBannerConfig`, { 
      headers: this.getHeaders() 
    });
    return this.handleResponse(res);
  }

  async saveBannerConfig(config) {
    const { contentType, body } = this.serializeBody(config);
    const headers = { ...this.getHeaders() };
    if (contentType) headers['content-type'] = contentType;
    const res = await fetch(`${this.url}/ArenaBannerConfig`, { method: 'POST', headers, body });
    return this.handleResponse(res);
  }

  validateConfig(config) {
    const errors = [];
    if (!config || typeof config !== 'object') {
      errors.push('Configuration must be a valid object');
      return errors;
    }
    if (config.messages && Array.isArray(config.messages)) {
      config.messages.forEach((message, index) => {
        if (!message.key || typeof message.key !== 'string') {
          errors.push(`Message ${index + 1}: key is required and must be a string`);
        }
        if (message.translations && typeof message.translations === 'object') {
          Object.entries(message.translations).forEach(([lang, translation]) => {
            if (translation && typeof translation !== 'string') {
              errors.push(`Message ${index + 1}: translation for ${lang} must be a string`);
            }
          });
        }
        if (message.rules && typeof message.rules === 'object') {
          if (message.rules.games && !Array.isArray(message.rules.games)) {
            errors.push(`Message ${index + 1}: rules.games must be an array`);
          }
        }
        if (message.domains && !Array.isArray(message.domains)) {
          errors.push(`Message ${index + 1}: domains must be an array`);
        }
        if (message.arenas && !Array.isArray(message.arenas)) {
          errors.push(`Message ${index + 1}: arenas must be an array`);
        }
      });
    }
    return errors;
  }
}
